import React, { useState, useEffect } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import CustomText from '../atoms/CustomText';
import BaseModal from './BaseModal';
import { colors, spacing, typography } from '../../utils/styling';
import SvgClipboard from '../icons/SvgClipboard';

type SendMode = 'link' | 'email' | 'launch';

const FIRM_DEFAULT_MSG_KEY = 'mp_firm_default_quiz_message';

const DEFAULT_MESSAGE =
  'I would like to invite you to take the Money Personality assessment. ' +
  'This short quiz will help us understand your financial mindset and ' +
  'tailor our approach to your goals.\n\n' +
  'Click the link below to get started:';

function loadFirmDefault(): string {
  try {
    return localStorage.getItem(FIRM_DEFAULT_MSG_KEY) || DEFAULT_MESSAGE;
  } catch {
    return DEFAULT_MESSAGE;
  }
}

function saveFirmDefault(msg: string) {
  try {
    localStorage.setItem(FIRM_DEFAULT_MSG_KEY, msg);
  } catch { /* localStorage full or unavailable */ }
}

interface SendQuizModalProps {
  isVisible: boolean;
  onClose: () => void;
  quizLink: string;
  clientName?: string;
  clientEmail?: string;
}

export default function SendQuizModal({
  isVisible,
  onClose,
  quizLink,
  clientName,
  clientEmail: initialEmail,
}: SendQuizModalProps) {
  const [mode, setMode] = useState<SendMode>('link');
  const [email, setEmail] = useState(initialEmail || '');
  const [message, setMessage] = useState(loadFirmDefault);
  const [copied, setCopied] = useState(false);
  const [savedDefault, setSavedDefault] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setEmail(initialEmail || '');
      setMessage(loadFirmDefault());
      setCopied(false);
      setSavedDefault(false);
    }
  }, [isVisible, initialEmail]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(quizLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = quizLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleSendEmail = () => {
    const subject = encodeURIComponent('Take Your Money Personality Assessment');
    const body = encodeURIComponent(`${message}\n\n${quizLink}`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_self');
  };

  const handleLaunchNow = () => {
    window.open(quizLink, '_blank', 'noopener');
    onClose();
  };

  const handleSaveDefault = () => {
    saveFirmDefault(message);
    setSavedDefault(true);
    setTimeout(() => setSavedDefault(false), 2000);
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: 6,
    cursor: 'pointer',
    backgroundColor: active ? colors.primary : 'transparent',
    border: active ? 'none' : `1px solid ${colors.gray[300]}`,
    transition: 'all 0.15s ease',
  });

  const tabTextStyle = (active: boolean) => ({
    color: active ? colors.white : colors.gray[700],
    fontWeight: '600' as const,
    fontSize: 14,
    fontFamily: typography.fontFamily.body,
  });

  return (
    <BaseModal isVisible={isVisible} onClose={onClose} title="Send Quiz" width={520}>
      {/* Mode tabs */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 24 }}>
        <Pressable style={tabStyle(mode === 'link')} onPress={() => setMode('link')}>
          <CustomText style={tabTextStyle(mode === 'link')}>Share Link</CustomText>
        </Pressable>
        <Pressable style={tabStyle(mode === 'email')} onPress={() => setMode('email')}>
          <CustomText style={tabTextStyle(mode === 'email')}>Email</CustomText>
        </Pressable>
        <Pressable style={tabStyle(mode === 'launch')} onPress={() => setMode('launch')}>
          <CustomText style={tabTextStyle(mode === 'launch')}>Launch Now</CustomText>
        </Pressable>
      </View>

      {/* SHARE LINK */}
      {mode === 'link' && (
        <View>
          <CustomText style={{
            fontSize: 14, color: colors.gray[600],
            fontFamily: typography.fontFamily.body, marginBottom: 12,
          }}>
            Copy this unique quiz link to share with {clientName || 'your client'}.
          </CustomText>
          <View style={{
            flexDirection: 'row', alignItems: 'center', gap: 8,
            backgroundColor: colors.gray[100], borderRadius: 8, padding: 12,
          }}>
            <CustomText style={{
              flex: 1, fontSize: 13, color: colors.dark,
              fontFamily: typography.fontFamily.body,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            } as any}>
              {quizLink}
            </CustomText>
            <Pressable
              onPress={handleCopy}
              style={{
                backgroundColor: copied ? colors.success : colors.primary,
                paddingTop: 8, paddingBottom: 8, paddingLeft: 16, paddingRight: 16,
                borderRadius: 6, cursor: 'pointer',
                flexDirection: 'row', alignItems: 'center', gap: 6,
                transition: 'all 0.15s ease',
              } as any}
            >
              <SvgClipboard color={colors.white} width={14} height={14} />
              <CustomText style={{
                color: colors.white, fontSize: 13, fontWeight: '600',
                fontFamily: typography.fontFamily.body,
              }}>
                {copied ? 'Copied!' : 'Copy'}
              </CustomText>
            </Pressable>
          </View>
        </View>
      )}

      {/* EMAIL */}
      {mode === 'email' && (
        <View>
          <CustomText style={{
            fontSize: 14, color: colors.gray[600],
            fontFamily: typography.fontFamily.body, marginBottom: 16,
          }}>
            Compose an email invitation. This opens your email client with the message below.
          </CustomText>

          {/* Client email */}
          <CustomText style={{
            fontSize: 12, fontWeight: '600', color: colors.gray[600],
            fontFamily: typography.fontFamily.body, marginBottom: 4,
          }}>
            Client email
          </CustomText>
          <TextInput
            value={email}
            onChangeText={setEmail}
            placeholder="client@example.com"
            placeholderTextColor={colors.gray[400]}
            style={{
              height: 40, borderWidth: 1, borderColor: colors.gray[300],
              borderRadius: 6, paddingLeft: 12, paddingRight: 12,
              fontFamily: typography.fontFamily.body, fontSize: 14,
              backgroundColor: colors.white, color: colors.dark,
              marginBottom: 16, outlineStyle: 'none', width: '100%',
            } as any}
          />

          {/* Message body */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <CustomText style={{
              fontSize: 12, fontWeight: '600', color: colors.gray[600],
              fontFamily: typography.fontFamily.body,
            }}>
              Message
            </CustomText>
            <Pressable onPress={handleSaveDefault} style={{ cursor: 'pointer' } as any}>
              <CustomText style={{
                fontSize: 12, color: savedDefault ? colors.success : colors.primary,
                fontFamily: typography.fontFamily.body, fontWeight: '600',
              }}>
                {savedDefault ? 'Saved as default!' : 'Save as firm default'}
              </CustomText>
            </Pressable>
          </View>
          <TextInput
            value={message}
            onChangeText={setMessage}
            multiline
            numberOfLines={5}
            style={{
              minHeight: 120, borderWidth: 1, borderColor: colors.gray[300],
              borderRadius: 6, padding: 12,
              fontFamily: typography.fontFamily.body, fontSize: 14,
              backgroundColor: colors.white, color: colors.dark,
              marginBottom: 8, outlineStyle: 'none', width: '100%',
              textAlignVertical: 'top', resize: 'vertical',
            } as any}
          />
          <CustomText style={{
            fontSize: 12, color: colors.gray[400],
            fontFamily: typography.fontFamily.body, marginBottom: 16,
          }}>
            The quiz link will be appended automatically.
          </CustomText>

          {/* Send button */}
          <Pressable
            onPress={handleSendEmail}
            style={{
              backgroundColor: !email.includes('@') ? colors.gray[400] : colors.primary,
              paddingTop: 12, paddingBottom: 12,
              borderRadius: 8, cursor: !email.includes('@') ? 'not-allowed' : 'pointer',
              alignItems: 'center',
              opacity: !email.includes('@') ? 0.6 : 1,
            } as any}
            disabled={!email.includes('@')}
          >
            <CustomText style={{
              color: colors.white, fontWeight: '600', fontSize: 16,
              fontFamily: typography.fontFamily.body,
            }}>
              Open in Email Client
            </CustomText>
          </Pressable>
        </View>
      )}

      {/* LAUNCH NOW */}
      {mode === 'launch' && (
        <View>
          <CustomText style={{
            fontSize: 14, color: colors.gray[600],
            fontFamily: typography.fontFamily.body, marginBottom: 8,
          }}>
            Open the quiz in a new window for in-person use with {clientName || 'your client'}.
          </CustomText>
          <CustomText style={{
            fontSize: 13, color: colors.gray[500],
            fontFamily: typography.fontFamily.body, marginBottom: 24,
          }}>
            Hand the screen to your client or share it on a meeting screen. Their results
            will appear in your portal once they complete the assessment.
          </CustomText>
          <Pressable
            onPress={handleLaunchNow}
            style={{
              backgroundColor: colors.primary,
              paddingTop: 14, paddingBottom: 14,
              borderRadius: 8, cursor: 'pointer', alignItems: 'center',
            }}
          >
            <CustomText style={{
              color: colors.white, fontWeight: '600', fontSize: 16,
              fontFamily: typography.fontFamily.body,
            }}>
              Launch Quiz Now
            </CustomText>
          </Pressable>
        </View>
      )}
    </BaseModal>
  );
}
